// ─────────────────────────────────────────────────────────────
// PrivacyPolicyModal.js
// Privacy policy covering SMS opt-in, data collection, and user rights.
// The organization is named from SITE (see site.config.js), never hardcoded:
// on a sister site this document must name that group, not this one.
// ─────────────────────────────────────────────────────────────

import React from 'react';
import SITE from '../site';

const LAST_UPDATED = 'April 2026';

// The body is exported on its own so the standalone /privacy route renders the
// same text as the footer modal. One source of truth — a second copy would drift,
// and this document is cited by URL in the toll-free verification filing.
export function PrivacyPolicyBody() {
  return (
    <>
        <div className="modal-header">
          <h2>Privacy Policy</h2>
          <p className="modal-org">{SITE.name} · Last updated {LAST_UPDATED}</p>
        </div>

        <div className="modal-waiver-text">

          <p>
            {SITE.name} ("we," "us," or "our") operates this website to coordinate
            community pickup ultimate frisbee games. This policy explains what information
            we collect, how we use it, and your rights.
          </p>

          <p><strong>1. Information We Collect</strong></p>
          <p>
            <em>RSVPs:</em> When you RSVP for a game, we collect your name and your
            response (yes, maybe, or no). We also collect a browser fingerprint — a
            technical identifier derived from your device and browser settings — to
            prevent duplicate RSVPs and to allow you to modify your response.
          </p>
          <p>
            <em>SMS opt-in:</em> If you voluntarily provide your phone number and check
            the opt-in box, we collect your phone number for the purpose of sending you
            game reminders, cancellation notices, and other game-related announcements.
            Providing your phone number is entirely optional.
          </p>
          <p>
            <em>Parental consent forms:</em> If you submit a parental consent form, we
            collect the information you provide on that form (parent name, contact
            information, child information, and emergency contacts).
          </p>
          <p>
            <em>Comments:</em> If you post in the discussion section, we collect your
            name and the text of your comment.
          </p>
          <p>
            <em>Device and technical information:</em> We collect IP address, device
            type, and general location information for security and audit purposes when
            RSVPs are submitted.
          </p>

          <p><strong>2. How We Use Your Information</strong></p>
          <p>
            We use the information we collect solely to operate this community game
            coordination service — specifically to display RSVP counts, communicate about
            games, maintain security, and comply with legal obligations. We do not use
            your information for advertising, profiling, or any commercial purpose.
          </p>

          <p><strong>3. SMS Communications</strong></p>
          <p>
            If you opt in to SMS communications, you consent to receive text messages
            from {SITE.name} at the phone number you provide. Message frequency
            varies based on game schedule, typically 1–3 messages per week during the
            season. Message and data rates may apply.
          </p>
          <p>
            <em>To opt out:</em> Reply STOP to any text message at any time. You will
            receive a confirmation and no further messages will be sent. To opt back in,
            reply START.
          </p>
          <p>
            We use a third-party SMS provider to deliver text messages. Your phone number
            is transmitted to that provider solely for message delivery, and is not used
            by them for any other purpose. We will update this policy if our provider
            changes.
          </p>

          <p><strong>4. Analytics</strong></p>
          <p>
            This site uses Google Analytics to understand how people find and use it —
            for example, whether a Reddit post brought visitors who then RSVPd.
            Analytics data is aggregated and anonymous; it is used only to improve the
            pickup experience. No accounts are required. IP addresses are anonymized.
            You can opt out using the{' '}
            <a href="https://tools.google.com/dlpage/gaoptout" target="_blank" rel="noopener noreferrer">
              Google Analytics opt-out browser add-on
            </a>.
          </p>

          <p><strong>5. Data Sharing</strong></p>
          <p>
            We do not sell, rent, or share your personal information with third parties,
            except as necessary to deliver this service (our SMS provider for text
            messages, Google Firebase for data storage and hosting, Google Analytics for
            anonymous usage data) or as required by law.
          </p>

          <p><strong>5. Data Storage and Security</strong></p>
          <p>
            Your information is stored using Google Firebase, which provides
            industry-standard security. Phone numbers are readable only by the group
            organizer: the publicly visible RSVP records contain a name and response
            only, and identify you by a one-way hash of your number rather than the
            number itself. We retain RSVP data and comments indefinitely as a record
            of community participation. You may request deletion at any time by
            contacting us.
          </p>

          <p><strong>6. Your Rights</strong></p>
          <p>
            You may request access to, correction of, or deletion of your personal
            information at any time. To exercise these rights, or to opt out of SMS
            without texting STOP, contact the group organizer directly.
          </p>

          <p><strong>7. Children</strong></p>
          <p>
            This site is intended for adults. Participation by minors requires a
            completed parental consent form. We do not knowingly collect personal
            information from children under 13 without parental consent.
          </p>

          <p><strong>8. Changes to This Policy</strong></p>
          <p>
            We may update this policy from time to time. The "last updated" date at the
            top will reflect any changes. Continued use of this site after changes
            constitutes acceptance of the updated policy.
          </p>

          <p><strong>9. Contact</strong></p>
          <p>
            Questions about this policy? Contact the {SITE.name} organizers through
            the website or by replying to any SMS communication.
          </p>

        </div>
    </>
  );
}

function PrivacyPolicyModal({ onClose }) {
  return (
    <div className="modal-overlay"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-box">
        <button className="modal-close" onClick={onClose}>×</button>
        <PrivacyPolicyBody />
        <button className="submit-btn" style={{marginTop:'1rem', width:'100%'}}
          onClick={onClose}>
          Close
        </button>
      </div>
    </div>
  );
}

export default PrivacyPolicyModal;
